import { useState, useEffect, useCallback, lazy, Suspense } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { useAuth } from "../contexts/AuthContext";
import logging from "../utils/logging";
import Header from "../components/Header";
import { Button } from "../components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Car, ScanLine, Building2, History, Boxes, BarChart3, Loader2 } from "lucide-react";

import { API } from "./epass/utils";
import EPassUpsell from "../components/EPassUpsell";

// Lazy-load each tab so we don't pay the cost of parsing/evaluating Fleet
// CSV logic, Analytics chart code, or Tesseract glue until the user actually
// clicks that tab. Radix Tabs only mounts the active <TabsContent>, so each
// chunk is only fetched on demand. Each module is a NAMED export — adapt
// each lazy() call so React.lazy gets a default-export shape.
const VehiclesTab = lazy(() => import("./epass/VehiclesTab").then((m) => ({ default: m.VehiclesTab })));
const ScanTab = lazy(() => import("./epass/ScanTab").then((m) => ({ default: m.ScanTab })));
const PremisesTab = lazy(() => import("./epass/PremisesTab").then((m) => ({ default: m.PremisesTab })));
const ScanHistoryTab = lazy(() => import("./epass/ScanHistoryTab").then((m) => ({ default: m.ScanHistoryTab })));
const FleetTab = lazy(() => import("./epass/FleetTab").then((m) => ({ default: m.FleetTab })));
const AnalyticsTab = lazy(() => import("./epass/AnalyticsTab").then((m) => ({ default: m.AnalyticsTab })));

function TabFallback() {
  const { t } = useTranslation();
  return (
    <div className="flex items-center justify-center py-12 text-sm text-gray-400 font-body" data-testid="tab-loading">
      <Loader2 className="w-4 h-4 mr-2 animate-spin" /> {t("common.loading")}
    </div>
  );
}

export default function EPassPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [premises, setPremises] = useState([]);
  const [scans, setScans] = useState([]);
  const subscribed = user?.epass_subscribed;
  const isAdmin = user?.role === "system_admin" || user?.role === "govt_admin";

  const fetchNotifications = useCallback(async () => {
    try { setNotifications((await axios.get(`${API}/notifications`, { withCredentials: true })).data); } catch { /* notifications are non-critical; ePass page still functional without them */ }
  }, []);
  const fetchVehicles = useCallback(async () => {
    try { setVehicles((await axios.get(`${API}/epass/vehicles`, { withCredentials: true })).data); } catch (err) { logging.error("Vehicles", err); }
  }, []);
  const fetchPremises = useCallback(async () => {
    try { setPremises((await axios.get(`${API}/epass/premises`, { withCredentials: true })).data); } catch (err) { logging.error("Premises", err); }
  }, []);
  const fetchScans = useCallback(async () => {
    try { setScans((await axios.get(`${API}/epass/scan-history`, { withCredentials: true })).data); } catch (err) { logging.error("Scans", err); }
  }, []);

  useEffect(() => {
    fetchNotifications();
    if (subscribed || isAdmin) { fetchVehicles(); fetchPremises(); fetchScans(); }
  }, [subscribed, isAdmin, fetchNotifications, fetchVehicles, fetchPremises, fetchScans]);

  // Flutterwave redirect handler — when the user returns from a vehicle
  // subscription payment we get `?vehicle_tx_ref=...&transaction_id=...`.
  // Verify the transaction, surface a toast, then strip the params from
  // the URL so a refresh doesn't re-verify.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const txRef = params.get("vehicle_tx_ref");
    const txId = params.get("transaction_id");
    if (!txRef) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(
          `${API}/epass/payments/verify/${txRef}${txId ? `?transaction_id=${txId}` : ""}`,
          { withCredentials: true },
        );
        if (cancelled) return;
        if (r.data?.status === "successful") {
          toast.success(t("epass.sub_activated"));
          fetchVehicles();
        } else {
          toast.error(t("epass.payment_pending"));
        }
      } catch (err) {
        if (!cancelled) toast.error(err.response?.data?.detail || t("epass.verify_failed"));
      } finally {
        // Strip query params so a refresh doesn't re-trigger the verify call.
        window.history.replaceState({}, "", "/epass");
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubscribe = async () => {
    try {
      const res = await axios.post(`${API}/epass/subscribe`, {}, { withCredentials: true });
      toast.success(res.data.epass_subscribed ? t("epass.subscribed_toast") : t("epass.unsubscribed_toast"));
      window.location.reload();
    } catch { toast.error(t("common.failed")); }
  };

  return (
    <div className="min-h-screen bg-[#F3F4F6]" data-testid="epass-page">
      <Header notifications={notifications} onNotificationRead={fetchNotifications} />

      <div className="p-4 md:p-6 max-w-6xl mx-auto">
        <div className="flex items-center justify-between gap-3 mb-6">
          <div className="flex items-center gap-3">
            <Car className="w-7 h-7 text-gray-900" strokeWidth={2} />
            <div>
              <h1 className="font-heading text-2xl sm:text-3xl font-black tracking-tighter text-gray-900">{t("epass.title")}</h1>
              <p className="text-sm text-gray-500 font-body">{t("epass.subtitle")}</p>
            </div>
          </div>
          {!isAdmin && (
            <Button
              onClick={handleSubscribe}
              variant={subscribed ? "outline" : "default"}
              className={`rounded-sm font-body text-sm ${subscribed ? "" : "bg-black hover:bg-gray-800 text-white"}`}
              data-testid="epass-subscribe-btn"
            >
              {subscribed ? t("epass.subscribed") : t("epass.subscribe_btn")}
            </Button>
          )}
        </div>

        {!subscribed && !isAdmin ? (
          <EPassUpsell onSubscribe={handleSubscribe} />
        ) : (
          <Tabs defaultValue="vehicles" className="space-y-4">
            {/* Scrollable tab strip — 6 tabs overflow small screens, so the
                list scrolls inside its own container instead of stretching
                the page sideways. */}
            <div className="overflow-x-auto max-w-full [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
              <TabsList className="bg-white border border-gray-200 rounded-sm p-0.5 w-max">
              <TabsTrigger value="vehicles" className="rounded-sm text-sm font-body data-[state=active]:bg-black data-[state=active]:text-white" data-testid="tab-vehicles">
                <Car className="w-4 h-4 mr-1.5" /> {t("epass.tab_vehicles")}
              </TabsTrigger>
              <TabsTrigger value="scan" className="rounded-sm text-sm font-body data-[state=active]:bg-black data-[state=active]:text-white" data-testid="tab-scan">
                <ScanLine className="w-4 h-4 mr-1.5" /> {t("epass.tab_scan")}
              </TabsTrigger>
              <TabsTrigger value="premises" className="rounded-sm text-sm font-body data-[state=active]:bg-black data-[state=active]:text-white" data-testid="tab-premises">
                <Building2 className="w-4 h-4 mr-1.5" /> {t("epass.tab_premises")}
              </TabsTrigger>
              <TabsTrigger value="history" className="rounded-sm text-sm font-body data-[state=active]:bg-black data-[state=active]:text-white" data-testid="tab-scan-history">
                <History className="w-4 h-4 mr-1.5" /> {t("epass.tab_history")}
              </TabsTrigger>
              <TabsTrigger value="fleet" className="rounded-sm text-sm font-body data-[state=active]:bg-black data-[state=active]:text-white" data-testid="tab-fleet">
                <Boxes className="w-4 h-4 mr-1.5" /> {t("epass.tab_fleet")}
              </TabsTrigger>
              {isAdmin && (
                <TabsTrigger value="analytics" className="rounded-sm text-sm font-body data-[state=active]:bg-black data-[state=active]:text-white" data-testid="tab-analytics">
                  <BarChart3 className="w-4 h-4 mr-1.5" /> {t("epass.tab_analytics")}
                </TabsTrigger>
              )}
            </TabsList>
            </div>

            <TabsContent value="vehicles">
              <Suspense fallback={<TabFallback />}>
                <VehiclesTab vehicles={vehicles} onRefresh={fetchVehicles} isAdmin={isAdmin} />
              </Suspense>
            </TabsContent>
            <TabsContent value="scan">
              <Suspense fallback={<TabFallback />}>
                <ScanTab premises={premises} />
              </Suspense>
            </TabsContent>
            <TabsContent value="premises">
              <Suspense fallback={<TabFallback />}>
                <PremisesTab premises={premises} onRefresh={fetchPremises} />
              </Suspense>
            </TabsContent>
            <TabsContent value="history">
              <Suspense fallback={<TabFallback />}>
                <ScanHistoryTab scans={scans} onRefresh={fetchScans} isAdmin={isAdmin} />
              </Suspense>
            </TabsContent>
            <TabsContent value="fleet">
              <Suspense fallback={<TabFallback />}>
                <FleetTab isAdmin={isAdmin} />
              </Suspense>
            </TabsContent>
            {isAdmin && (
              <TabsContent value="analytics">
                <Suspense fallback={<TabFallback />}>
                  <AnalyticsTab />
                </Suspense>
              </TabsContent>
            )}
          </Tabs>
        )}
      </div>
    </div>
  );
}
